
import { Link } from "react-router-dom";
import { Plus, Tags, MoreVertical, Eye, Edit, Settings } from "lucide-react";
import { ActionsDropdown } from "@/components/ui/actions-dropdown";
import { Profile } from "@/types/profile";
import { PlatformIcons } from "./PlatformIcons";
import { Button } from "@/components/ui/button";
import CustomerTag from "@/components/CustomerTag";
import { mockCustomers } from "@/utils/customerGroups";
import { useState } from "react";
import { TagAssignmentDialog } from "@/components/tags/TagAssignmentDialog";
import { TagsDisplay } from "@/components/tags/TagsDisplay";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ColumnDef, ColumnSizingState } from "@tanstack/react-table";
import { DataTable } from "@/components/ui/data-table";
import { SortableHeader } from "@/components/ui/table-cells";
import {
  GlassCard,
  GlassCardContent,
  GlassCardDescription,
  GlassCardFooter,
  GlassCardHeader,
  GlassCardTitle,
} from "@/components/ui/glass-card";
import { getIconComponent } from "@/utils/profileIcons";

interface ProfilesListProps {
  profiles: Profile[];
  view: "grid" | "list";
  searchQuery?: string;
}

export const ProfilesList = ({ profiles, view, searchQuery = "" }: ProfilesListProps) => {
  const [isTagAssignmentOpen, setIsTagAssignmentOpen] = useState(false);
  const [selectedProfileForTags, setSelectedProfileForTags] = useState<{ id: string; name: string } | null>(null);
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});

  // Helper function to get customer name by ID
  const getCustomerName = (id: string) => {
    return mockCustomers.find(c => c.id === id)?.name || "Not Available";
  };

  const openTagAssignment = (profile: Profile) => {
    setSelectedProfileForTags({ id: profile.id, name: profile.name });
    setIsTagAssignmentOpen(true);
  };

  const columns: ColumnDef<Profile>[] = [
    {
      accessorKey: "icon",
      header: "",
      cell: ({ row }) => {
        const ProfileIcon = row.original.icon 
          ? getIconComponent(row.original.icon) 
          : Settings;
        return (
          <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center">
            <ProfileIcon className="h-4 w-4 text-primary" />
          </div>
        );
      },
      size: 50,
      enableSorting: false,
    },
    {
      accessorKey: "name",
      header: ({ column }) => <SortableHeader column={column}>Name</SortableHeader>,
      cell: ({ row }) => (
        <div className="font-medium">
          <Link 
            to={`/profiles/${row.original.id}`}
            className="hover:text-primary underline-offset-4 hover:underline"
          >
            {row.getValue("name")}
          </Link>
          {row.original.isDraft && (
            <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-500 px-2 py-0.5 rounded-full">
              Draft
            </span>
          )}
        </div>
      ),
    },
    {
      accessorKey: "description",
      header: ({ column }) => <SortableHeader column={column}>Description</SortableHeader>,
      cell: ({ row }) => <div>{row.getValue("description")}</div>,
    },
    {
      accessorKey: "platforms",
      header: "Platforms",
      cell: ({ row }) => (
        <PlatformIcons platforms={row.getValue("platforms")} size="sm" />
      ),
    },
    {
      accessorKey: "customers",
      header: "Customers",
      cell: ({ row }) => {
        const profile = row.original;
        return (
          <div className="flex flex-wrap gap-1">
            {profile.customers && profile.customers.length > 0 ? (
              profile.customers.map((customerId) => (
                <CustomerTag key={customerId} customer={getCustomerName(customerId)} />
              ))
            ) : profile.customer ? (
              <CustomerTag customer={getCustomerName(profile.customer)} />
            ) : (
              <CustomerTag customer="Not Available" />
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "payloads",
      header: ({ column }) => <SortableHeader column={column}>Payloads</SortableHeader>,
      cell: ({ row }) => (
        <div>{row.original.payloads.length}</div>
      ),
    },
    {
      id: "tags",
      header: ({ column }) => <SortableHeader column={column}>Tags</SortableHeader>,
      cell: ({ row }) => (
        <TagsDisplay 
          resourceId={row.original.id} 
          resourceType="profile" 
          size="sm" 
          maxTags={3}
          customerIds={row.original.customers}
          assignedTagIds={row.original.tags}
        />
      ),
    },
    {
      id: "actions",
      header: () => <div className="text-right">Actions</div>,
      cell: ({ row }) => {
        const profile = row.original;
        return (
          <ActionsDropdown
            actions={[
              {
                label: "View",
                icon: Eye,
                onClick: () => window.location.href = `/profiles/${profile.id}`,
              },
              {
                label: "Edit",
                icon: Edit,
                onClick: () => window.location.href = `/profiles/${profile.id}/edit`,
              },
              {
                label: "Assign Tags",
                icon: Tags,
                onClick: () => openTagAssignment(profile),
              },
            ]}
          />
        );
      },
    },
  ];

  // Filter profiles based on search query
  const filteredProfiles = profiles.filter((profile) =>
    profile.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    profile.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    profile.platforms.some(platform => platform.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (filteredProfiles.length === 0) {
    return (
      <div className="text-center py-12">
        {profiles.length === 0 ? (
          <>
            <p className="text-muted-foreground text-lg">No profiles found</p>
            <div className="mt-4">
              <Button asChild>
                <Link to="/profiles/new">
                  <Plus className="mr-2 h-4 w-4" />
                  Create your first profile
                </Link>
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-muted-foreground text-lg">No profiles match your search.</p>
            <p className="text-sm text-muted-foreground mt-1">
              Try adjusting your search criteria.
            </p>
          </>
        )}
      </div>
    );
  }

  const content = view === "grid" ? (
    <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
      {filteredProfiles.map((profile) => {
        const ProfileIcon = profile.icon ? getIconComponent(profile.icon) : Settings;
        
        return (
          <GlassCard key={profile.id} variant="default">
            <GlassCardHeader className="pb-2 p-4">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <ProfileIcon className="h-5 w-5 text-primary" />
                  </div>
                  <Link 
                    to={`/profiles/${profile.id}`}
                    className="text-lg font-semibold hover:text-primary transition-colors block"
                  >
                    {profile.name}
                  </Link>
                </div>
                <ActionsDropdown
                  actions={[
                    {
                      label: "View",
                      icon: Eye,
                      onClick: () => window.location.href = `/profiles/${profile.id}`,
                    },
                    {
                      label: "Edit",
                      icon: Edit,
                      onClick: () => window.location.href = `/profiles/${profile.id}/edit`,
                    },
                    {
                      label: "Assign Tags",
                      icon: Tags,
                      onClick: () => openTagAssignment(profile),
                    },
                  ]}
                />
              </div>
              <GlassCardDescription className="mt-1">{profile.description}</GlassCardDescription>
            </GlassCardHeader>
            <GlassCardContent className="space-y-2 p-4 pt-0">
              <p className="text-sm text-muted-foreground">
                {profile.payloads.length} payload{profile.payloads.length !== 1 ? 's' : ''}
              </p>
              <div className="flex flex-wrap gap-2">
                {profile.customers && profile.customers.length > 0 ? (
                  profile.customers.map((customerId) => (
                    <CustomerTag key={customerId} customer={getCustomerName(customerId)} />
                  ))
                ) : profile.customer ? (
                  <CustomerTag customer={getCustomerName(profile.customer)} />
                ) : (
                  <CustomerTag customer="Not Available" />
                )}
              </div>
              {profile.isDraft && (
                <span className="text-xs bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-500 px-2.5 py-1 rounded-full">
                  Draft
                </span>
              )}
              <TagsDisplay 
                resourceId={profile.id} 
                resourceType="profile" 
                maxTags={3}
                customerIds={profile.customers}
                assignedTagIds={profile.tags}
              />
              <div className="mt-2 pt-2 border-t">
                <PlatformIcons platforms={profile.platforms} />
              </div>
            </GlassCardContent>
          </GlassCard>
        );
      })}
    </div>
  ) : (
    <DataTable
      data={filteredProfiles}
      columns={columns}
      enableRowSelection={true}
      enableColumnResizing={true}
      enableSorting={true}
      columnSizing={columnSizing}
      onColumnSizingChange={setColumnSizing}
      emptyMessage={
        profiles.length === 0 
          ? "No profiles found. Create your first profile to get started."
          : "No profiles match your search. Try adjusting your search criteria."
      }
    />
  );

  return (
    <>
      {content}
      
      {selectedProfileForTags && (
        <TagAssignmentDialog
          open={isTagAssignmentOpen}
          onOpenChange={setIsTagAssignmentOpen}
          resourceId={selectedProfileForTags.id}
          resourceType="profile"
          resourceName={selectedProfileForTags.name}
          onAssignmentComplete={() => {
            setSelectedProfileForTags(null);
            // Optionally refresh profile data to show updated tags
          }}
        />
      )}
    </>
  );
};
